/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Detalle del cheque + acciones (deposit / endorse / reject / return_to_client).
 * Compartido entre Caja → Cartera y Clientes → ficha (sección Cheques).
 * El parent provee `submit` (server-authoritative, idempotente vía operationId).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatLocalDate, formatLocalDateTime } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { webOperationalScrollBindings } from '../../shared/theme/webScrollbar';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader';
import { ModalHeroHeader } from '../../shared/modals/ModalHeroHeader';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { AttachmentPreviewModal } from '../../shared/attachments/components/AttachmentPreviewModal';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import {
  checkDueBadge,
  checkDueBadgeLabel,
  CHECK_STATUS_LABELS,
  type CheckCommandOperation,
  type CheckRow,
} from '../infra/checksRepo';
import { supabase } from '../../shared/supabase/supabaseClient';
// Leaves directos (no barrels): cash/ui-api re-exporta este componente y los
// barrels de customers/sales/expenses vuelven a cash → require cycle.
// Allowlists en check-module-boundaries.mjs.
import {
  listCustomerMovementAttachments,
  createCustomerMovementAttachmentUrl,
  type CustomerMovementAttachmentRow,
} from '../../customers/infra/customerMovementAttachmentsRepo';
import {
  listSaleAttachments,
  createSaleAttachmentUrl,
  type SaleAttachmentRow,
} from '../../sales/infra/saleAttachmentsRepo';
import { listExpensesForBranch } from '../../expenses/infra/expensesRepoRead';
import type { ExpenseRow } from '../../expenses/domain/expenseTypes';
import { formatMoney } from './workspace/cashManagement.utils';

export type CheckActionSubmitParams = {
  operation: CheckCommandOperation;
  checkId: string;
  settlementId?: string;
  expenseId?: string;
  reason?: string;
  note?: string;
  successMessage: string;
};

export type CheckActionSubmit = (params: CheckActionSubmitParams) => Promise<boolean>;

type CheckAttachment = {
  id: string;
  fileName: string;
  fileUrl: string | null;
  mimeType: string;
  signedUrl: () => Promise<string | null>;
};

type ActionTarget = {
  check: CheckRow;
  kind: 'deposit' | 'return_to_client' | 'back_to_portfolio';
} | null;

type Props = {
  check: CheckRow | null;
  todayIso: string;
  customerName: string | null;
  canEdit: boolean;
  canEndorse: boolean;
  canReject: boolean;
  submitting: boolean;
  submit: CheckActionSubmit;
  onClose: () => void;
};

export function CheckDetailActions({
  check,
  todayIso,
  customerName,
  canEdit,
  canEndorse,
  canReject,
  submitting,
  submit,
  onClose,
}: Props) {
  const [confirmTarget, setConfirmTarget] = useState<ActionTarget>(null);
  const [rejectTarget, setRejectTarget] = useState<CheckRow | null>(null);
  const [endorseTarget, setEndorseTarget] = useState<CheckRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const backdropPadding = useModalBackdropPadding();

  const submitAndClose = useCallback(
    async (params: CheckActionSubmitParams, onOk: () => void) => {
      const ok = await submit(params);
      if (ok) {
        onOk();
        onClose();
      }
    },
    [submit, onClose],
  );

  const confirmCopy = (() => {
    if (!confirmTarget) return { title: '', message: '', label: '' };
    if (confirmTarget.kind === 'deposit') {
      return {
        title: 'Depositar cheque',
        message: `Marcás el cheque N° ${confirmTarget.check.number ?? ''} como depositado.`,
        label: 'Depositado',
      };
    }
    if (confirmTarget.kind === 'return_to_client') {
      return {
        title: 'Devolver cheque al cliente',
        message: 'Le devolvés el cheque al cliente. Se revierte el cobro y vuelve la deuda.',
        label: 'Devuelto',
      };
    }
    if (confirmTarget.check.status === 'endorsed') {
      return {
        title: 'Volver a cartera',
        message: 'El cheque vuelve a tu cartera y se anula el pago del gasto.',
        label: 'Volver a cartera',
      };
    }
    if (confirmTarget.check.status === 'rejected') {
      return {
        title: 'Volver a cartera',
        message: 'Se deshace el rechazo: el cobro vuelve a valer y el cheque vuelve a tu cartera.',
        label: 'Volver a cartera',
      };
    }
    return {
      title: 'Volver a cartera',
      message: 'El cheque vuelve a tu cartera. El cobro sigue registrado.',
      label: 'Volver a cartera',
    };
  })();

  // Los sub-modales van como overlay DENTRO del Modal de detalle — un segundo
  // Modal nativo queda detrás en web (el usuario ve el backdrop sin contenido).
  const overlay = (
    <>
      <OperationalConfirmModal
        visible={confirmTarget != null}
        presentation="overlay"
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmLabel={confirmCopy.label}
        loading={submitting}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => {
          if (!confirmTarget) return;
          const successMessage =
            confirmTarget.kind === 'deposit'
              ? 'Cheque marcado como depositado'
              : confirmTarget.kind === 'return_to_client'
                ? 'Cheque devuelto al cliente'
                : 'Cheque de vuelta en cartera';
          void submitAndClose(
            {
              operation: confirmTarget.kind,
              checkId: confirmTarget.check.id,
              successMessage,
            },
            () => setConfirmTarget(null),
          );
        }}
      />

      <CheckRejectOverlay
        check={rejectTarget}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        submitting={submitting}
        onClose={() => setRejectTarget(null)}
        onConfirm={() => {
          if (!rejectTarget) return;
          void submitAndClose(
            {
              operation: 'reject',
              checkId: rejectTarget.id,
              reason: rejectReason.trim() || undefined,
              successMessage: 'Cheque rechazado',
            },
            () => setRejectTarget(null),
          );
        }}
      />

      <CheckEndorseModal
        check={endorseTarget}
        branchId={endorseTarget?.branchId ?? null}
        submitting={submitting}
        onClose={() => setEndorseTarget(null)}
        onConfirm={(expense, note) => {
          if (!endorseTarget) return;
          void submitAndClose(
            {
              operation: 'endorse',
              checkId: endorseTarget.id,
              settlementId: crypto.randomUUID(),
              expenseId: expense.id,
              note: note || undefined,
              successMessage: `Cheque endosado a ${expense.supplierName ?? 'proveedor'}`,
            },
            () => setEndorseTarget(null),
          );
        }}
      />
    </>
  );

  return (
    <CheckDetailModal
      check={check}
      todayIso={todayIso}
      visible={check != null}
      backdropPadding={backdropPadding}
      customerName={customerName}
      canEdit={canEdit}
      canEndorse={canEndorse}
      canReject={canReject}
      submitting={submitting}
      onClose={onClose}
      onDeposit={(c) => setConfirmTarget({ check: c, kind: 'deposit' })}
      onReturn={(c) => setConfirmTarget({ check: c, kind: 'return_to_client' })}
      onBackToPortfolio={(c) => setConfirmTarget({ check: c, kind: 'back_to_portfolio' })}
      onReject={(c) => {
        setRejectReason('');
        setRejectTarget(c);
      }}
      onEndorse={(c) => setEndorseTarget(c)}
      overlay={overlay}
    />
  );
}

/** Rechazo con motivo — capa overlay dentro del modal de detalle. */
function CheckRejectOverlay({
  check,
  reason,
  onReasonChange,
  submitting,
  onClose,
  onConfirm,
}: {
  check: CheckRow | null;
  reason: string;
  onReasonChange: (value: string) => void;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!check) return null;

  return (
    <View style={modalExperience.confirmOverlayRoot} pointerEvents="box-none">
      <Pressable
        style={modalExperience.confirmOverlayBackdrop}
        onPress={() => {
          if (!submitting) onClose();
        }}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      />
      <View
        style={[
          posUi.modalCard,
          modalShellShared.modalCardShell,
          modalExperience.modalWidthStandard,
          localStyles.modalCard,
          localStyles.overlayCard,
        ]}
        onStartShouldSetResponder={() => true}
      >
        <ModalScreenHeader title="Rechazar cheque" onClose={onClose} />
        <ModalHeroHeader
          kicker="Confirmación"
          subtitle={
            check.customerId
              ? 'Se revierte el cobro: vuelve la deuda y las ventas quedan pendientes. Si estaba endosado, se reabre el gasto.'
              : 'El cheque queda marcado como rechazado.'
          }
        />
        <View style={modalExperience.formPanel}>
          <ModalFormField label="Motivo">
            <TextInput
              value={reason}
              onChangeText={onReasonChange}
              placeholder="Falta de fondos, cheque robado…"
              placeholderTextColor={ticketColors.ink3}
              style={[modalExperience.formInput, modalExperience.formInputMultiline]}
              multiline
              editable={!submitting}
              testID="caja.cartera.reject-reason"
            />
          </ModalFormField>
        </View>
        <View style={localStyles.actions}>
          <TouchableOpacity
            style={[styles.secondaryButton, localStyles.actionBtn]}
            onPress={onClose}
            disabled={submitting}
            accessibilityRole="button"
          >
            <Text style={styles.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.primaryButton, localStyles.actionBtn, { backgroundColor: ticketColors.bad }]}
            onPress={onConfirm}
            disabled={submitting}
            accessibilityRole="button"
            testID="caja.cartera.reject-confirm"
          >
            <Text style={styles.primaryButtonText}>
              {submitting ? 'Rechazando…' : 'Rechazar'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

function CheckDetailModal({
  check,
  todayIso,
  visible,
  backdropPadding,
  customerName,
  canEdit,
  canEndorse,
  canReject,
  submitting,
  onClose,
  onDeposit,
  onReturn,
  onReject,
  onEndorse,
  onBackToPortfolio,
  overlay,
}: {
  check: CheckRow | null;
  todayIso: string;
  visible: boolean;
  backdropPadding: object;
  customerName: string | null;
  canEdit: boolean;
  canEndorse: boolean;
  canReject: boolean;
  submitting: boolean;
  onClose: () => void;
  onDeposit: (check: CheckRow) => void;
  onReturn: (check: CheckRow) => void;
  onReject: (check: CheckRow) => void;
  onEndorse: (check: CheckRow) => void;
  onBackToPortfolio: (check: CheckRow) => void;
  overlay?: React.ReactNode;
}) {
  const [attachments, setAttachments] = useState<CheckAttachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState('');
  const [previewMime, setPreviewMime] = useState('application/octet-stream');

  useEffect(() => {
    if (!check) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    setAttachmentsLoading(true);
    void (async () => {
      try {
        if (check.sourceType === 'customer_payment') {
          const rows = await listCustomerMovementAttachments(check.sourceId);
          if (cancelled) return;
          setAttachments(
            rows.map((a: CustomerMovementAttachmentRow) => ({
              id: a.id,
              fileName: a.fileName,
              fileUrl: a.fileUrl,
              mimeType: a.mimeType,
              signedUrl: () => createCustomerMovementAttachmentUrl(a.fileUrl ?? ''),
            })),
          );
        } else {
          const { data: pay } = await supabase
            .schema('pos')
            .from('sale_payments')
            .select('sale_id')
            .eq('id', check.sourceId)
            .maybeSingle();
          const saleId = pay?.sale_id != null ? String(pay.sale_id) : null;
          if (!saleId || cancelled) {
            if (!cancelled) setAttachments([]);
            return;
          }
          const rows = await listSaleAttachments(saleId);
          if (cancelled) return;
          setAttachments(
            rows.map((a: SaleAttachmentRow) => ({
              id: a.id,
              fileName: a.fileName,
              fileUrl: a.fileUrl,
              mimeType: a.mimeType,
              signedUrl: () => createSaleAttachmentUrl(a.fileUrl ?? ''),
            })),
          );
        }
      } finally {
        if (!cancelled) setAttachmentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [check]);

  if (!check) return null;
  const badge = check.status === 'in_portfolio' ? checkDueBadge(check.dueDate, todayIso) : null;

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            localStyles.modalCard,
          ]}
        >
          <ModalScreenHeader title={`Cheque N° ${check.number}`} onClose={onClose} />
          <ModalHeroHeader
            kicker={CHECK_STATUS_LABELS[check.status]}
            title={formatMoney(check.amount)}
            subtitle={
              badge ? checkDueBadgeLabel(badge, check.dueDate, todayIso) : undefined
            }
          />
          <ModalAwareScrollView
            {...webOperationalScrollBindings({ flexShrink: 1 })}
            keyboardShouldPersistTaps="handled"
          >
            <View style={localStyles.detailGrid} testID="caja.cartera.detail">
              <DetailRow label="Librador" value={check.drawer ?? '—'} />
              <DetailRow label="Banco" value={check.bank ?? '—'} />
              <DetailRow
                label="Vencimiento"
                value={check.dueDate ? formatLocalDate(check.dueDate) : '—'}
              />
              <DetailRow
                label="Origen"
                value={check.sourceType === 'customer_payment' ? 'Cobro de cuenta' : 'Venta POS'}
              />
              {customerName ? <DetailRow label="Cliente" value={customerName} /> : null}
              {check.rejectionReason ? (
                <DetailRow label="Motivo de rechazo" value={check.rejectionReason} />
              ) : null}
              {check.depositedAt ? (
                <DetailRow label="Depositado" value={formatLocalDateTime(check.depositedAt)} />
              ) : null}
              {check.endorsedAt ? (
                <DetailRow label="Endosado" value={formatLocalDateTime(check.endorsedAt)} />
              ) : null}
              {check.rejectedAt ? (
                <DetailRow label="Rechazado" value={formatLocalDateTime(check.rejectedAt)} />
              ) : null}
              {check.returnedAt ? (
                <DetailRow label="Devuelto" value={formatLocalDateTime(check.returnedAt)} />
              ) : null}
            </View>

            <Text style={modalExperience.formSectionTitle}>Comprobantes</Text>
            {attachmentsLoading ? (
              <View style={modalExperience.stateBox}>
                <ActivityIndicator color={ticketColors.gold} />
                <Text style={modalExperience.stateText}>Cargando…</Text>
              </View>
            ) : attachments.length > 0 ? (
              <View style={localStyles.attachmentList}>
                {attachments.map((a) => (
                  <TouchableOpacity
                    key={a.id}
                    style={localStyles.attachmentRow}
                    onPress={() => {
                      if (!a.fileUrl) return;
                      void a.signedUrl().then((url) => {
                        if (!url) return;
                        setPreviewName(a.fileName);
                        setPreviewMime(a.mimeType);
                        setPreviewUri(url);
                      });
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Ver ${a.fileName}`}
                    testID={`caja.cartera.attachment.${a.id}`}
                  >
                    <Ionicons
                      name={a.mimeType.startsWith('image/') ? 'image-outline' : 'document-text-outline'}
                      size={20}
                      color={ticketColors.ink2}
                    />
                    <View style={localStyles.attachmentTextCol}>
                      <Text style={localStyles.attachmentFileName} numberOfLines={1}>
                        {a.fileName}
                      </Text>
                      <Text style={localStyles.attachmentMeta}>Ver comprobante</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <Text style={localStyles.noAttachments}>Sin comprobantes adjuntos.</Text>
            )}
          </ModalAwareScrollView>

          <View style={localStyles.actions} testID="caja.cartera.detail.actions">
            {check.status === 'in_portfolio' && canEdit ? (
              <TouchableOpacity
                style={[styles.primaryButton, localStyles.actionBtn]}
                onPress={() => onDeposit(check)}
                disabled={submitting}
                accessibilityRole="button"
                testID="caja.cartera.action.deposit"
              >
                <Text style={styles.primaryButtonText}>Depositado</Text>
              </TouchableOpacity>
            ) : null}
            {check.status === 'in_portfolio' && canEndorse ? (
              <TouchableOpacity
                style={[styles.secondaryButton, localStyles.actionBtn]}
                onPress={() => onEndorse(check)}
                disabled={submitting}
                accessibilityRole="button"
                testID="caja.cartera.action.endorse"
              >
                <Text style={styles.secondaryButtonText}>Endosar</Text>
              </TouchableOpacity>
            ) : null}
            {(check.status === 'in_portfolio' || check.status === 'deposited' || check.status === 'endorsed') &&
            canReject ? (
              <TouchableOpacity
                style={[styles.secondaryButton, localStyles.actionBtn, { borderColor: ticketColors.bad }]}
                onPress={() => onReject(check)}
                disabled={submitting}
                accessibilityRole="button"
                testID="caja.cartera.action.reject"
              >
                <Text style={[styles.secondaryButtonText, { color: ticketColors.bad }]}>Rechazar</Text>
              </TouchableOpacity>
            ) : null}
            {(check.status === 'in_portfolio' || check.status === 'rejected') && canEdit ? (
              <TouchableOpacity
                style={[
                  styles.secondaryButton,
                  localStyles.actionBtn,
                  { backgroundColor: ticketColors.warnBg, borderColor: ticketColors.warn },
                ]}
                onPress={() => onReturn(check)}
                disabled={submitting}
                accessibilityRole="button"
                testID="caja.cartera.action.return"
              >
                <Text style={[styles.secondaryButtonText, { color: ticketColors.warn }]}>
                  Devolver al cliente
                </Text>
              </TouchableOpacity>
            ) : null}
            {(check.status === 'deposited' || check.status === 'endorsed' || check.status === 'rejected') &&
            canEdit ? (
              <TouchableOpacity
                style={[styles.secondaryButton, localStyles.actionBtn]}
                onPress={() => onBackToPortfolio(check)}
                disabled={submitting}
                accessibilityRole="button"
                testID="caja.cartera.action.back-to-portfolio"
              >
                <Text style={styles.secondaryButtonText}>Volver a cartera</Text>
              </TouchableOpacity>
            ) : null}
          </View>

          <AttachmentPreviewModal
            visible={previewUri != null}
            uri={previewUri}
            fileName={previewName}
            mimeType={previewMime}
            onClose={() => setPreviewUri(null)}
          />
        </View>
        {overlay}
      </View>
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={localStyles.detailRow}>
      <Text style={localStyles.detailLabel}>{label}</Text>
      <Text style={localStyles.detailValue}>{value}</Text>
    </View>
  );
}

/** Endoso → picker de gasto con saldo pendiente (deuda de proveedor). */
function CheckEndorseModal({
  check,
  branchId,
  submitting,
  onClose,
  onConfirm,
}: {
  check: CheckRow | null;
  branchId: string | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (expense: ExpenseRow, note: string) => void;
}) {
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!check || !branchId) return;
    setNote('');
    setLoading(true);
    void (async () => {
      try {
        const rows = await listExpensesForBranch({ branchId, paymentStatus: 'unpaid' });
        const partial = await listExpensesForBranch({ branchId, paymentStatus: 'partial' });
        setExpenses(
          [...rows, ...partial].sort((a, b) => b.amount - a.amount),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [check, branchId]);

  if (!check) return null;

  // Overlay dentro del Modal de detalle (no un Modal hermano): en web un segundo
  // Modal nativo queda detrás del detalle.
  return (
    <View style={modalExperience.confirmOverlayRoot} pointerEvents="box-none">
      <Pressable
        style={modalExperience.confirmOverlayBackdrop}
        onPress={() => {
          if (!submitting) onClose();
        }}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      />
      <View
        style={[
          posUi.modalCard,
          modalShellShared.modalCardShell,
          modalExperience.modalWidthStandard,
          localStyles.modalCard,
          localStyles.overlayCard,
        ]}
        onStartShouldSetResponder={() => true}
      >
          <ModalScreenHeader title="Endosar cheque" onClose={onClose} />
          <ModalHeroHeader
            kicker={`Cheque N° ${check.number}`}
            title={formatMoney(check.amount)}
            subtitle="Elegí el gasto que pagás con este cheque. Sale de la cartera, no del cajón."
          />
          {loading ? (
            <View style={modalExperience.stateBox}>
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={modalExperience.stateText}>Cargando gastos…</Text>
            </View>
          ) : expenses.length === 0 ? (
            <ModalStatePanel
              empty
              emptyTitle="Sin gastos pendientes"
              emptyMessage="No hay gastos sin pagar o parciales en esta sucursal."
            />
          ) : (
            <ModalAwareScrollView
              {...webOperationalScrollBindings({ flexShrink: 1, maxHeight: 320 })}
              keyboardShouldPersistTaps="handled"
            >
              <View style={modalExperience.listContainer} testID="caja.cartera.endorse.list">
                {expenses.map((expense) => {
                  const residual = Math.max(0, expense.amount - expense.amountPaid);
                  return (
                    <TouchableOpacity
                      key={expense.id}
                      style={localStyles.row}
                      onPress={() => onConfirm(expense, note.trim())}
                      disabled={submitting}
                      accessibilityRole="button"
                      testID={`caja.cartera.endorse.expense.${expense.id}`}
                    >
                      <View style={localStyles.rowMain}>
                        <Text style={modalExperience.listRowTitle} numberOfLines={1}>
                          {expense.supplierName ?? expense.description ?? expense.categoryName ?? 'Gasto'}
                        </Text>
                        <Text style={modalExperience.listMeta} numberOfLines={1}>
                          {expense.categoryName ?? ''}
                          {expense.dueDate ? ` · vence ${formatLocalDate(expense.dueDate)}` : ''}
                        </Text>
                      </View>
                      <View style={localStyles.rowAside}>
                        <Text style={localStyles.rowAmount}>{formatMoney(residual)}</Text>
                        <Text style={localStyles.residualHint}>pendiente</Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ModalAwareScrollView>
          )}
          <View style={modalExperience.formPanel}>
            <ModalFormField label="Nota (opcional)">
              <TextInput
                value={note}
                onChangeText={setNote}
                placeholder="Ej: pago parcial de mercadería"
                placeholderTextColor={ticketColors.ink3}
                style={modalExperience.formInput}
                editable={!submitting}
                testID="caja.cartera.endorse-note"
              />
            </ModalFormField>
          </View>
      </View>
    </View>
  );
}

const localStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowAside: {
    alignItems: 'flex-end',
  },
  rowAmount: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  residualHint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  modalCard: {
    maxHeight: '92%',
    gap: spacing.sm,
  },
  /** Card de overlay: encima del backdrop absoluteFill. */
  overlayCard: {
    zIndex: 1,
    elevation: 1,
  },
  detailGrid: {
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  detailLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  detailValue: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
    flexShrink: 1,
    textAlign: 'right',
  },
  attachmentList: {
    gap: spacing.xs,
  },
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  attachmentTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  attachmentFileName: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  attachmentMeta: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  noAttachments: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  actionBtn: {
    flexGrow: 1,
    flexBasis: '45%',
  },
});
